import { Component, Input, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ConfirmPopupModule } from 'primeng/confirmpopup';
import { TooltipModule } from 'primeng/tooltip';
import { ToastModule } from 'primeng/toast';
import { TagModule } from 'primeng/tag';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { Textarea } from 'primeng/textarea';
import { YamadaStock } from '../../models/yamada-product';
import { ConfirmationService, MessageService } from 'primeng/api';
import {
  DisplayCancelDonkiStockGQL,
  DisplayCancelEdionStockGQL,
  DisplayCancelYamadaStockGQL,
  DisplayDonkiStockGQL,
  DisplayEdionStockGQL,
  DisplayYamadaStockGQL,
  FoundDonkiStockGQL,
  FoundEdionStockGQL,
  FoundYamadaStockGQL,
  NotFoundDonkiStockGQL,
  NotFoundEdionStockGQL,
  NotFoundYamadaStockGQL,
  PurchaseCancelDonkiStockGQL,
  PurchaseCancelEdionStockGQL,
  PurchaseCancelYamadaStockGQL,
  PurchaseDonkiStockGQL,
  PurchaseEdionStockGQL,
  PurchaseYamadaStockGQL,
  UpsertDonkiProductMemoGQL,
  UpsertEdionProductMemoGQL,
  UpsertYamadaProductMemoGQL,
  ConfirmDonkiStockGQL,
  ConfirmCancelDonkiStockGQL,
  SetDonkiStockFlagGQL,
  SetEdionStockFlagGQL,
  SetYamadaStockFlagGQL,
  StockFlag,
} from 'src/gql/generated';
import { firstValueFrom } from 'rxjs';
import { EdionStock } from 'src/app/models/edion-product';
import { UserService } from 'src/app/services/user.service';
import { DonkiStock } from 'src/app/models/donki-product';
import { isSoldOutByPurchase } from 'src/app/utils/purchase-state';

interface BaseStock {
  type: 'yamada' | 'edion' | 'donki';
  id: string;
  isConfirmed?: boolean;
  confirmedUserName?: string;
  confirmedAt?: string;
}

type Stock = (YamadaStock | EdionStock | DonkiStock) & BaseStock;

/**
 * 6 段階のボタン（2026-10-14 ユーザー指定: 購入・展示・規制・見送・店頭無・店員無）のうち、
 * setXxxStockFlag でまとめて扱う 3 つ。購入・展示・店頭無（isNotFound）は従来どおり個別に扱う
 */
export type FlagButton = {
  flag: StockFlag;
  /** ボタンに出す短い名前 */
  label: string;
  /** カーソルを当てた時の説明 */
  description: string;
  keys: {
    on: 'isRestricted' | 'isSkipped' | 'isStaffNoStock';
    user: 'restrictedUserName' | 'skippedUserName' | 'staffNoStockUserName';
    at: 'restrictedAt' | 'skippedAt' | 'staffNoStockAt';
  };
};

export const FLAG_BUTTONS: readonly FlagButton[] = [
  {
    flag: StockFlag.Restricted,
    label: '規制',
    description: '出品制限があって仕入れを見送った',
    keys: { on: 'isRestricted', user: 'restrictedUserName', at: 'restrictedAt' },
  },
  {
    flag: StockFlag.Skipped,
    label: '見送',
    description: '条件を見て仕入れを見送った',
    keys: { on: 'isSkipped', user: 'skippedUserName', at: 'skippedAt' },
  },
  {
    flag: StockFlag.StaffNoStock,
    label: '店員無',
    description: '店員に聞いたが在庫が無かった',
    keys: { on: 'isStaffNoStock', user: 'staffNoStockUserName', at: 'staffNoStockAt' },
  },
];

@Component({
  selector: 'app-spreadsheet-stock-badge',
  templateUrl: './spreadsheet-stock-badge.component.html',
  styleUrls: ['./spreadsheet-stock-badge.component.sass'],
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    DialogModule,
    ConfirmPopupModule,
    TooltipModule,
    ToastModule,
    TagModule,
    InputTextModule,
    InputNumberModule,
    Textarea,
    DatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [ConfirmationService, MessageService],
})
export class SpreadsheetStockBadgeComponent implements OnInit {
  @Input() stocks: Stock[];
  @Input() showPurchased: boolean;
  @Input() showDisplay: boolean;

  memoModel: Stock = {} as Stock;
  showMemo = false;

  constructor(
    private confirmationService: ConfirmationService,
    private messageService: MessageService,
    private userService: UserService,
    private yamadaMemoGQL: UpsertYamadaProductMemoGQL,
    private edionMemoGQL: UpsertEdionProductMemoGQL,
    private donkiMemoGQL: UpsertDonkiProductMemoGQL,
    private purchaseYamadaGQL: PurchaseYamadaStockGQL,
    private purchaseEdionGQL: PurchaseEdionStockGQL,
    private purchaseDonkiGQL: PurchaseDonkiStockGQL,
    private purchaseCancelYamadaGQL: PurchaseCancelYamadaStockGQL,
    private purchaseCancelEdionGQL: PurchaseCancelEdionStockGQL,
    private purchaseCancelDonkiGQL: PurchaseCancelDonkiStockGQL,
    private displayYamadaGQL: DisplayYamadaStockGQL,
    private displayEdionGQL: DisplayEdionStockGQL,
    private displayDonkiGQL: DisplayDonkiStockGQL,
    private displayCancelYamadaGQL: DisplayCancelYamadaStockGQL,
    private displayCancelEdionGQL: DisplayCancelEdionStockGQL,
    private displayCancelDonkiGQL: DisplayCancelDonkiStockGQL,
    private notFoundYamadaGQL: NotFoundYamadaStockGQL,
    private notFoundEdionGQL: NotFoundEdionStockGQL,
    private notFoundDonkiGQL: NotFoundDonkiStockGQL,
    private foundYamadaGQL: FoundYamadaStockGQL,
    private foundEdionGQL: FoundEdionStockGQL,
    private foundDonkiGQL: FoundDonkiStockGQL,
    private confirmDonkiGQL: ConfirmDonkiStockGQL,
    private confirmCancelDonkiGQL: ConfirmCancelDonkiStockGQL,
    private setFlagYamadaGQL: SetYamadaStockFlagGQL,
    private setFlagEdionGQL: SetEdionStockFlagGQL,
    private setFlagDonkiGQL: SetDonkiStockFlagGQL,
  ) {}

  ngOnInit(): void {}

  getStyle(hasStock: boolean): any {
    if (hasStock) {
      return null;
    } else {
      return {
        background: 'gray',
      };
    }
  }

  getClass(stock: Stock): string {
    if (this.showPurchased && this.showDisplay) {
      return '';
    }

    if (!this.showPurchased && isSoldOutByPurchase(stock)) {
      return 'display: none;'; // 購入で売り切れた店だけ隠す。在庫が残る店は出す
    }

    if (!this.showDisplay && stock.isDisplay) {
      return 'display: none;';
    }

    return '';
  }

  getStoreName(code: string, name: string): string {
    const append = code ? '[' + code + ']' : '';
    return append + name.trim();
  }

  // --------------------------------------------------------
  //                                                 Purchase
  // --------------------------------------------------------

  /** 購入の個数入力（2026-10-17 ユーザー指定: 何個買ったかをメモではなくボタンで持つ）。初期値 1 */
  showPurchase = false;
  purchaseModel: Stock = {} as Stock;
  purchaseQuantity = 1;
  /** 個数の上限＝その店の在庫数（在庫数が無い・0 なら 1）。在庫より多くは買えない（2026-10-17 ユーザー指定） */
  purchaseMax = 1;

  /**
   * 購入ボタン: 個数（初期値 1）を聞いてから登録する。
   * 在庫が 1 の店（在庫数が無い店も）は聞くまでもないので、画面を出さずにそのまま 1 個で登録する（2026-10-17 ユーザー指定）
   */
  async confirm(_event: Event, stock: Stock): Promise<void> {
    this.purchaseModel = stock;
    this.purchaseQuantity = 1;
    this.purchaseMax = Math.max(1, Math.floor(Number(stock.stockNum) || 0));
    if (this.purchaseMax <= 1) {
      await this.doPurchase();
      return;
    }
    this.showPurchase = true;
  }

  /** 個数が 1〜在庫数に収まっているか。外れていれば「購入」は押せず、送りもしない */
  isQuantityValid(): boolean {
    const q = Number(this.purchaseQuantity);
    return Number.isInteger(q) && q >= 1 && q <= this.purchaseMax;
  }

  async doPurchase(): Promise<void> {
    if (!this.isQuantityValid()) {
      return;
    }
    const stock = this.purchaseModel;
    const quantity = Math.floor(Number(this.purchaseQuantity));
    let result = false;
    switch (stock.type) {
      case 'yamada':
        result = await this.purchaseYamada(stock.id, quantity);
        break;
      case 'edion':
        result = await this.purchaseEdion(stock.id, quantity);
        break;
      case 'donki':
        result = await this.purchaseDonki(stock.id, quantity);
        break;
      default:
        console.log('unknown stock type: ', stock);
    }
    if (result) {
      stock.isPurchased = true;
      stock.purchasedUserName = this.userService.getName();
      stock.purchasedQuantity = quantity > 1 ? quantity : null;
      stock.purchasedAt = new Date().toISOString();
    }
    this.showPurchase = false;
  }

  /** ボタンに出す個数。1 個でも「1個」と出す（2026-10-17 ユーザー指定: 何個買ったか画面で分かるように）。個数が無い古い記録は 1 個扱い */
  quantityLabel(stock: Stock): string {
    const q = stock.purchasedQuantity ?? 1;
    return ` ${q}個`;
  }

  confirmCancel(event: Event, stock: Stock): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'この商品の購入をキャンセルしますか？',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        var result = false;
        switch (stock.type) {
          case 'yamada':
            result = await this.purchaseCancelYamada(stock.id);
            break;
          case 'edion':
            result = await this.purchaseCancelEdion(stock.id);
            break;
          case 'donki':
            result = await this.purchaseCancelDonki(stock.id);
            break;
          default:
            console.log('unknown stock type: ', stock);
        }
        if (result) {
          stock.isPurchased = false;
          stock.purchasedUserName = null;
        }
      },
      reject: () => {},
    });
  }

  confirmDisplayCancel(event: Event, stock: Stock): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'この商品の展示をキャンセルしますか？',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        var result = false;
        switch (stock.type) {
          case 'yamada':
            result = await this.displayCancelYamada(stock.id);
            break;
          case 'edion':
            result = await this.displayCancelEdion(stock.id);
            break;
          case 'donki':
            result = await this.displayCancelDonki(stock.id);
            break;
          default:
            console.log('unknown stock type: ', stock);
        }
        if (result) {
          stock.isDisplay = false;
          stock.displayUserName = null;
        }
      },
      reject: () => {},
    });
  }

  async purchaseYamada(id: string, quantity = 1): Promise<boolean> {
    const resp = await firstValueFrom(this.purchaseYamadaGQL.mutate({ variables: { id, quantity } }));
    if (resp.data?.purchaseYamadaStock) {
      this.showSuccess();
      return true;
    }
    this.showError();
    return false;
  }

  async purchaseCancelYamada(id: string): Promise<boolean> {
    const resp = await firstValueFrom(this.purchaseCancelYamadaGQL.mutate({ variables: { id } }));
    if (resp.data?.purchaseCancelYamadaStock) {
      this.showSuccess(true);
      return true;
    }
    this.showError();
    return false;
  }

  async purchaseEdion(id: string, quantity = 1): Promise<boolean> {
    const resp = await firstValueFrom(this.purchaseEdionGQL.mutate({ variables: { id, quantity } }));
    if (resp.data?.purchaseEdionStock) {
      this.showSuccess();
      return true;
    }
    this.showError();
    return false;
  }

  async purchaseCancelEdion(id: string): Promise<boolean> {
    const resp = await firstValueFrom(this.purchaseCancelEdionGQL.mutate({ variables: { id } }));
    if (resp.data?.purchaseCancelEdionStock) {
      this.showSuccess(true);
      return true;
    }
    this.showError();
    return false;
  }

  async purchaseDonki(id: string, quantity = 1): Promise<boolean> {
    const resp = await firstValueFrom(this.purchaseDonkiGQL.mutate({ variables: { id, quantity } }));
    if (resp.data?.purchaseDonkiStock) {
      this.showSuccess();
      return true;
    }
    this.showError();
    return false;
  }

  async purchaseCancelDonki(id: string): Promise<boolean> {
    const resp = await firstValueFrom(this.purchaseCancelDonkiGQL.mutate({ variables: { id } }));
    if (resp.data?.purchaseCancelDonkiStock) {
      this.showSuccess(true);
      return true;
    }
    this.showError();
    return false;
  }

  // --------------------------------------------------------
  //                                                 Display
  // --------------------------------------------------------

  confirmDisplay(event: Event, stock: Stock): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'この商品を展示品に設定しますか？',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        var result = false;
        switch (stock.type) {
          case 'yamada':
            result = await this.displayYamada(stock.id);
            break;
          case 'edion':
            result = await this.displayEdion(stock.id);
            break;
          case 'donki':
            result = await this.displayDonki(stock.id);
            break;
          default:
            console.log('unknown stock type: ', stock);
        }
        if (result) {
          stock.isDisplay = true;
          stock.displayUserName = this.userService.getName();
        }
      },
      reject: () => {},
    });
  }

  async displayYamada(id: string): Promise<boolean> {
    const resp = await firstValueFrom(this.displayYamadaGQL.mutate({ variables: { id } }));
    if (resp.data?.displayYamadaStock) {
      this.showSuccessDisplay();
      return true;
    }
    this.showError();
    return false;
  }

  async displayEdion(id: string): Promise<boolean> {
    const resp = await firstValueFrom(this.displayEdionGQL.mutate({ variables: { id } }));
    if (resp.data?.displayEdionStock) {
      this.showSuccessDisplay();
      return true;
    }
    this.showError();
    return false;
  }

  async displayDonki(id: string): Promise<boolean> {
    const resp = await firstValueFrom(this.displayDonkiGQL.mutate({ variables: { id } }));
    if (resp.data?.displayDonkiStock) {
      this.showSuccessDisplay();
      return true;
    }
    this.showError();
    return false;
  }

  showSuccessDisplay(): void {
    this.messageService.add({
      severity: 'info',
      summary: '成功',
      detail: '対象の在庫を展示品に変更しました',
      life: 3000,
    });
  }

  async displayCancelYamada(id: string): Promise<boolean> {
    const resp = await firstValueFrom(this.displayCancelYamadaGQL.mutate({ variables: { id } }));
    if (resp.data?.displayCancelYamadaStock) {
      this.showSuccessDisplayCancel();
      return true;
    }
    this.showError();
    return false;
  }

  async displayCancelEdion(id: string): Promise<boolean> {
    const resp = await firstValueFrom(this.displayCancelEdionGQL.mutate({ variables: { id } }));
    if (resp.data?.displayCancelEdionStock) {
      this.showSuccessDisplayCancel();
      return true;
    }
    this.showError();
    return false;
  }

  async displayCancelDonki(id: string): Promise<boolean> {
    const resp = await firstValueFrom(this.displayCancelDonkiGQL.mutate({ variables: { id } }));
    if (resp.data?.displayCancelDonkiStock) {
      this.showSuccessDisplayCancel();
      return true;
    }
    this.showError();
    return false;
  }

  showSuccessDisplayCancel(): void {
    this.messageService.add({
      severity: 'info',
      summary: '成功',
      detail: '対象の在庫の展示をキャンセルしました',
      life: 3000,
    });
  }

  showSuccessNotFound(isFound: boolean = false): void {
    const partial = isFound ? '店頭無ではない状態' : '店頭無';
    this.messageService.add({
      severity: 'info',
      summary: '成功',
      detail: `対象の在庫を${partial}に変更しました`,
      life: 3000,
    });
  }

  showSuccessConfirm(isCancel: boolean = false): void {
    const partial = isCancel ? '確認済ではない状態' : '確認済';
    this.messageService.add({
      severity: 'info',
      summary: '成功',
      detail: `対象の在庫を${partial}に変更しました`,
      life: 3000,
    });
  }

  confirmDonki(event: Event, stock: Stock): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'この商品を確認済にしますか？',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        const resp = await firstValueFrom(this.confirmDonkiGQL.mutate({ variables: { id: stock.id } }));
        if (resp.data?.confirmDonkiStock) {
          this.showSuccessConfirm();
          stock.isConfirmed = true;
          stock.confirmedUserName = this.userService.getName();
          return true;
        }
        this.showError();
        return false;
      },
      reject: () => {},
    });
  }

  confirmCancelDonki(event: Event, stock: Stock): void {
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: 'この商品の確認済を解除しますか？',
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        const resp = await firstValueFrom(this.confirmCancelDonkiGQL.mutate({ variables: { id: stock.id } }));
        if (resp.data?.confirmCancelDonkiStock) {
          this.showSuccessConfirm(true);
          stock.isConfirmed = false;
          stock.confirmedUserName = null;
          return true;
        }
        this.showError();
        return false;
      },
      reject: () => {},
    });
  }

  // --------------------------------------------------------
  //                                                     Memo
  // --------------------------------------------------------

  openMemo(stock: Stock) {
    this.memoModel = stock;
    this.showMemo = true;
  }

  async doRegisterMemo() {
    var result = false;
    switch (this.memoModel.type) {
      case 'yamada':
        result = await this.registerMemoYamada();
        break;
      case 'edion':
        result = await this.registerMemoEdion();
        break;
      case 'donki':
        result = await this.registerMemoDonki();
        break;
      default:
        console.log('unknown stock type: ', this.memoModel);
    }
    if (result) {
      this.memoModel = {} as Stock;
      this.showMemo = false;
    }
  }

  async registerMemoYamada(): Promise<boolean> {
    const resp = await firstValueFrom(
      this.yamadaMemoGQL.mutate({
        variables: {
          id: this.memoModel.id,
          memo: this.memoModel.memo,
        },
      }),
    );
    if (resp.data?.upsertYamadaProductMemo) {
      this.showSuccessMemo();
      return true;
    }
    this.showError();
    return false;
  }

  async registerMemoEdion(): Promise<boolean> {
    const resp = await firstValueFrom(
      this.edionMemoGQL.mutate({
        variables: {
          id: this.memoModel.id,
          memo: this.memoModel.memo,
        },
      }),
    );
    if (resp.data?.upsertEdionProductMemo) {
      this.showSuccessMemo();
      return true;
    }
    this.showError();
    return false;
  }

  async registerMemoDonki(): Promise<boolean> {
    const resp = await firstValueFrom(
      this.donkiMemoGQL.mutate({
        variables: {
          id: this.memoModel.id,
          memo: this.memoModel.memo,
        },
      }),
    );
    if (resp.data?.upsertDonkiProductMemo) {
      this.showSuccessMemo();
      return true;
    }
    this.showError();
    return false;
  }

  showSuccessMemo(): void {
    this.messageService.add({
      severity: 'info',
      summary: '成功',
      detail: 'メモを登録しました',
      life: 3000,
    });
  }

  // --------------------------------------------------------
  //                                                 NotFound
  // --------------------------------------------------------

  confirmNotFound(event: Event, stock: Stock): void {
    const action = stock.type === 'donki' ? '確認' : '店頭無';
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `この商品を${action}にしますか？`,
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        var result = false;
        switch (stock.type) {
          case 'yamada':
            result = await this.notFoundYamada(stock.id);
            break;
          case 'edion':
            result = await this.notFoundEdion(stock.id);
            break;
          case 'donki':
            result = await this.notFoundDonki(stock.id);
            break;
          default:
            console.log('unknown stock type: ', stock);
        }
        if (result) {
          stock.isNotFound = true;
        }
      },
      reject: () => {},
    });
  }

  confirmFound(event: Event, stock: Stock): void {
    const action = stock.type === 'donki' ? '確認' : '店頭無';
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: `この商品の${action}をキャンセルしますか？`,
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        var result = false;
        switch (stock.type) {
          case 'yamada':
            result = await this.foundYamada(stock.id);
            break;
          case 'edion':
            result = await this.foundEdion(stock.id);
            break;
          case 'donki':
            result = await this.foundDonki(stock.id);
            break;
          default:
            console.log('unknown stock type: ', stock);
        }
        if (result) {
          stock.isNotFound = false;
        }
      },
      reject: () => {},
    });
  }

  async notFoundYamada(id: string): Promise<boolean> {
    const resp = await firstValueFrom(this.notFoundYamadaGQL.mutate({ variables: { id } }));
    if (resp.data?.notFoundYamadaStock) {
      this.showSuccessNotFound(false);
      return true;
    }
    this.showError();
    return false;
  }

  async foundYamada(id: string): Promise<boolean> {
    const resp = await firstValueFrom(this.foundYamadaGQL.mutate({ variables: { id } }));
    if (resp.data?.foundYamadaStock) {
      this.showSuccessNotFound(true);
      return true;
    }
    this.showError();
    return false;
  }

  async notFoundEdion(id: string): Promise<boolean> {
    const resp = await firstValueFrom(this.notFoundEdionGQL.mutate({ variables: { id } }));
    if (resp.data?.notFoundEdionStock) {
      this.showSuccessNotFound(false);
      return true;
    }
    this.showError();
    return false;
  }

  async foundEdion(id: string): Promise<boolean> {
    const resp = await firstValueFrom(this.foundEdionGQL.mutate({ variables: { id } }));
    if (resp.data?.foundEdionStock) {
      this.showSuccessNotFound(true);
      return true;
    }
    this.showError();
    return false;
  }

  async notFoundDonki(id: string): Promise<boolean> {
    const resp = await firstValueFrom(this.notFoundDonkiGQL.mutate({ variables: { id } }));
    if (resp.data?.notFoundDonkiStock) {
      this.showSuccessNotFound(false);
      return true;
    }
    this.showError();
    return false;
  }

  async foundDonki(id: string): Promise<boolean> {
    const resp = await firstValueFrom(this.foundDonkiGQL.mutate({ variables: { id } }));
    if (resp.data?.foundDonkiStock) {
      this.showSuccessNotFound(true);
      return true;
    }
    this.showError();
    return false;
  }

  showSuccess(isCancel: boolean = false): void {
    const partial = isCancel ? 'キャンセル' : '購入済み';
    this.messageService.add({
      severity: 'info',
      summary: '成功',
      detail: `対象の在庫を${partial}に変更しました`,
      life: 3000,
    });
  }

  // --------------------------------------------------------
  //                                                   Common
  // --------------------------------------------------------

  showError(): void {
    this.messageService.add({
      severity: 'error',
      summary: 'エラーが発生しました',
      detail: '継続する場合は管理者までお問い合わせください',
      life: 3000,
    });
  }

  // --------------------------------------------------------
  //                                 規制・見送・店員無
  // --------------------------------------------------------

  readonly flagButtons = FLAG_BUTTONS;

  /**
   * ボタンの見た目（2026-10-15 ユーザー指定: 押したものが一目で分かるように）。
   * まだ押していないボタンは全部うすいグレー、押したボタンは全部赤＋チェック印＋名前(日付)
   */
  readonly offStyle = { background: '#E5E7EB', color: '#374151' };
  readonly onStyle = { background: '#DC2626', color: 'white', fontWeight: 'bold' };

  isFlagOn(stock: Stock, button: FlagButton): boolean {
    return !!stock[button.keys.on];
  }

  flagUserName(stock: Stock, button: FlagButton): string {
    return stock[button.keys.user] ?? '';
  }

  flagAt(stock: Stock, button: FlagButton): string {
    return stock[button.keys.at] ?? '';
  }

  /** 付いていなければ付ける、付いていれば外す（確認してから） */
  confirmFlag(event: Event, stock: Stock, button: FlagButton): void {
    const on = !this.isFlagOn(stock, button);
    this.confirmationService.confirm({
      target: event.target as EventTarget,
      message: on ? `この商品を${button.label}にしますか？` : `この商品の${button.label}を解除しますか？`,
      icon: 'pi pi-exclamation-triangle',
      accept: async () => {
        const result = await this.setFlag(stock, button, on);
        if (result) {
          stock[button.keys.on] = on;
          stock[button.keys.user] = on ? this.userService.getName() : '';
          stock[button.keys.at] = on ? new Date().toISOString() : '';
        }
      },
      reject: () => {},
    });
  }

  async setFlag(stock: Stock, button: FlagButton, on: boolean): Promise<boolean> {
    const vars = { id: stock.id, flag: button.flag, on };
    let ok = false;
    switch (stock.type) {
      case 'yamada':
        ok = !!(await firstValueFrom(this.setFlagYamadaGQL.mutate({ variables: vars }))).data?.setYamadaStockFlag;
        break;
      case 'edion':
        ok = !!(await firstValueFrom(this.setFlagEdionGQL.mutate({ variables: vars }))).data?.setEdionStockFlag;
        break;
      case 'donki':
        ok = !!(await firstValueFrom(this.setFlagDonkiGQL.mutate({ variables: vars }))).data?.setDonkiStockFlag;
        break;
      default:
        console.log('unknown stock type: ', stock);
        return false;
    }
    if (ok) {
      this.showSuccessFlag(button.label, on);
    } else {
      this.showError();
    }
    return ok;
  }

  showSuccessFlag(label: string, on: boolean): void {
    this.messageService.add({
      severity: 'info',
      summary: '成功',
      detail: on ? `対象の在庫を${label}に変更しました` : `対象の在庫の${label}を解除しました`,
      life: 3000,
    });
  }
}
